import {
  WSButton,
  WSElement,
  WSElementProps,
  WSForm,
  WSText
} from "@wingspanhq/fe-component-library";
import { ICheckbookCard } from "@wingspanhq/payments/dist/interfaces";
import { AxiosError } from "axios";
import React from "react";
import * as Yup from "yup";
import { useWSMutation } from "../../query/helpers";
import { useUserId } from "../../query/hooks/helpers";
import { QUERY_INSTANT_PAYOUT } from "../../query/payments/keys";
import { useMemberProfile, useUserProfile } from "../../query/users/queries";
import { getUserLegalName } from "../../query/users/selectors";
import { WSQueries } from "../../query/WSQuery";
import { paymentsService } from "../../services/payments";
import {
  AddressField,
  addressToValues,
  AddressValues,
  addressValuesToRequest
} from "../../Settings/components/AddressField/AddressField";
import { CardField } from "../../Settings/components/CardField/CardField";
import { validatorAddress } from "../../utils/validators";
import { WSErrorMessage } from "../WSErrorMessage/WSErrorMessage";

type Props = {
  onSubmit?: (debitCard: ICheckbookCard) => void;
} & Omit<WSElementProps, "onSubmit">;

type AddDebitCardFormValues = {
  card: {
    cardNumber: string;
    expYYYY: string;
    expMM: string;
    cvv: string;
    name: string;
  };
  address: AddressValues;
};

export const AddDebitCardForm: React.FC<Props> = ({
  onSubmit,
  ...elementProps
}) => {
  const userId = useUserId();
  const memberQuery = useMemberProfile(userId);
  const userQuery = useUserProfile(userId);

  const [submit, submitMeta] = useWSMutation<
    ICheckbookCard,
    AxiosError,
    AddDebitCardFormValues
  >(
    async ({ card, address }) => {
      return await paymentsService.payoutSettings.debitCard.create(userId, {
        ...card,
        address: addressValuesToRequest(address)
      });
    },
    {
      dependencies: [QUERY_INSTANT_PAYOUT],
      onSuccess: onSubmit
    }
  );

  return (
    <WSElement {...elementProps}>
      <WSQueries
        queries={{
          memberQuery,
          userQuery
        }}
      >
        {({ memberQuery: { data: member }, userQuery: { data: user } }) => (
          <WSForm<AddDebitCardFormValues>
            defaultValues={{
              card: {
                name: getUserLegalName(user),
                cardNumber: "",
                expYYYY: "",
                expMM: "",
                cvv: ""
              },
              address: member.profile.address
                ? addressToValues(member.profile.address)
                : {
                    country: "US",
                    addressLine1: "",
                    addressLine2: "",
                    city: "",
                    state: "",
                    zipCode: ""
                  }
            }}
            // TODO: add validation!!
            validationSchema={Yup.object().shape({
              // cardNumber: validatorCard,
              // expYYYY: "2030",
              // expMM: "01",
              // name: "Lukas",
              address: validatorAddress
            })}
            onSubmit={submit}
          >
            <WSText.Heading3 mb="M">Card Details</WSText.Heading3>
            <CardField name="card" mb="L" />

            <WSText.Heading3 mb="M">Billing Address</WSText.Heading3>
            <AddressField name="address" mb="M" />

            <WSErrorMessage
              my="M"
              contextKey="AddDebitCard"
              forceShowApiErrors
              error={submitMeta.error}
            />

            <WSButton
              name="submitDebitCard"
              fullWidth
              type="submit"
              loading={submitMeta.isLoading}
            >
              Continue
            </WSButton>
          </WSForm>
        )}
      </WSQueries>
    </WSElement>
  );
};
